import { Meeting } from '../models/chat';
import { CacheService } from './cache.service';
import { ChatConversationService, PUBLIC_CHAT_ID } from './chat-conversation.service';
import { ChatMeetingService } from './chat-meeting.service';
import { InitialMeetingProviderService } from './initial-meeting-provider.service';
import { MeetingsStore } from './meetings.store';
import { OnlineStateService } from './online-state.service';
import { PageVisibilityService } from './page-visibility.service';
import { ChatUserStatusService } from './chat-user-status.service';
import { AuthServiceInterface } from './AuthServiceInterface';

export class ChatService {
    static $inject = [
        'MeetingsStore',
        'WebSocket',
        'ChatMeetingService',
        'CacheService',
        'ChatConversationService',
        'OnlineStateService',
        'PageVisibilityService',
        'InitialMeetingProviderService',
        'AuthService',
        'ChatUserStatusService',
    ];

    private meetings$: Rx.Observable<Meeting[]>;

    /**
     * Is the chat connected to the server?
     * We need to be connected to websockets server to receive messages.
     *
     * This status needs to be updated from outer sources - see constructor.
     *
     * @private
     */
    private isConnected$ = new Rx.BehaviorSubject<boolean>(true);

    private log(...args: any[]) {
        console.log.apply(console, ['ChatService', ...args]);
    }

    constructor(
        private meetingsStore: MeetingsStore,
        private webSocket: WebSocketService,
        private chatMeetingService: ChatMeetingService,
        private cacheService: CacheService,
        private conversationService: ChatConversationService,
        private onlineStateService: OnlineStateService,
        private pageVisibilityService: PageVisibilityService,
        private initialMeetingProviderService: InitialMeetingProviderService,
        private authService: AuthServiceInterface,
        private chatUserStatusService: ChatUserStatusService
    ) {
        this.initializeStore();
        // fetch meetings
        const meetings$ = this.chatMeetingService.getMeetings();

        this.meetings$ = this.cacheService.cached$('meetings_list', this.meetingsStore.meetings$);

        // Combine connection status of WebSockets and general internet connection into one,
        // but we probably don't have to look at connection info, since WS would get disconnected anyway.
        // I'm not sure if the WS will report it's disconnection status as fast as the window.navigator.
        this.webSocket.isConnected$
            .combineLatest(this.onlineStateService.isOnline$)
            .subscribe(([wsConnected, isOnline]) => {
                this.isConnected$.onNext(wsConnected && isOnline);
            });

        this.chatUserStatusService.init();
        this.setUpWebSocketKey(meetings$, this.isConnected$);
        this.setUpSyncAfterNetworkOnline(this.isConnected$);
        this.setUpUnreadMessageTracking();
        this.setUpInitialMeeting();
    }

    private setUpInitialMeeting() {
        this.meetings$.take(1).subscribe(async (meetings) => {
            const initialMeetingId = await this.initialMeetingProviderService.getInitialMeetingId();

            // we need to map the activeMeeting value here manually, as the getActiveMeeting$ filters the null value
            this.meetingsStore
                .getStore$()
                .take(1)
                .subscribe(({ activeMeeting }) => {
                    if (activeMeeting) {
                        return;
                    }

                    const findMeeting = (id: number) => meetings.find((m) => m.id === id);

                    const meeting = findMeeting(initialMeetingId ?? PUBLIC_CHAT_ID);
                    if (meeting) {
                        return this.setActiveMeeting(meeting);
                    }

                    const fallback = findMeeting(this.initialMeetingProviderService.getFallbackMeetingId());

                    fallback && this.setActiveMeeting(fallback);
                });
        });
    }

    private setUpSyncAfterNetworkOnline(connectionStatus$: Rx.Observable<boolean>) {
        // listen for connection status of the chat, we are only interested in truthy "online" status.
        connectionStatus$.filter(Boolean).subscribe(() => {
            // refresh meetings list, so we also update the unread counter
            this.chatMeetingService
                .fetchMeetings()
                .then((meetings) => this.meetingsStore.dispatchSetMeetings(meetings));
            // refresh messages for active meeting
            this.getActiveMeeting$()
                .take(1)
                .subscribe((m) => m && this.conversationService.dispatchFetchMessages(m.id));
        });
    }

    /**
     * Emit the Chat Key to the websocket server. The key tells the server what rooms we can join.
     * Sending the key is necessary in order to receive messages from the chat rooms.
     *
     * The key needs to be sent on each (re)connect, because our SocketID might change.
     *
     * @param meetings$ Observable of meetings, so we can collect and send a list of rooms we want to join.
     * @param  connectionStatus$ We need to send the key on reconnect
     * @private
     */
    private setUpWebSocketKey(meetings$: Rx.Observable<Meeting[]>, connectionStatus$: Rx.Observable<boolean>) {
        // Chat key is a must have here.
        const chatKey$ = Rx.Observable.fromPromise(this.chatMeetingService.getChatKey());
        const someMeetings$ = meetings$
            // we don't care about empty meeting list
            .filter((meetings) => meetings.length > 0);
        const isOnline$ = connectionStatus$.filter(Boolean);

        chatKey$.combineLatest(someMeetings$, isOnline$).subscribe(([chatKey, meetings, isOnline]) => {
            // push Public Chat ID
            const ids = [0, ...meetings.map((m) => m.id)];
            console.log('Joining chat rooms.', ids);
            this.webSocket.emit('join:chat', ids, chatKey);
        });
    }

    /**
     * Sets up conditions required to consistently update the unread-bar marker on the backend
     * as well as update local unread message counters.
     */
    private setUpUnreadMessageTracking() {
        // Emits a Meeting, when its latest message changes
        const newMessageInAMeeting$ = this.meetingsStore.meetings$
            // Makes an observable for each meeting, and emits the meeting on its corresponding observable
            .flatMap((meetings) => meetings)
            .groupBy((meeting) => meeting.id)
            .map((meeting$) => {
                return (
                    meeting$
                        // Emit further when the latest message changes == we got a new message in this meeting
                        .distinctUntilChanged((meeting) => meeting.last_message?.id)
                        // Skip first emitted value, otherwise we will update every meeting on page init
                        .skip(1)
                );
            })
            // Merge the distinct meeting observables so that we have one stream of changed meetings
            .mergeAll()
            // We don't care about messages sent by the same user
            .filter((meeting) => meeting.last_message?.type === 'message')
            .filter((meeting) => meeting.last_message?.owner?.id !== this.authService.getUserId());

        const isStuckToBottom$ = this.meetingsStore.isStuckToBottom$;

        const activeMeeting$ = this.meetingsStore.activeMeeting$.distinctUntilChanged((meeting) => meeting?.id);

        const windowIsFocused$ = this.pageVisibilityService.isWindowFocused$;

        // Filters
        const meetingIsActive = (meeting: Meeting) =>
            this.meetingsStore.getStore$().getValue().activeMeeting?.id === meeting.id;
        const windowIsFocused = () => this.pageVisibilityService.isWindowFocused;
        const isStuckToBottom = () => this.meetingsStore.getStore$().getValue().isStuckToBottom;

        // Effects
        const bumpLocalCounter = (meeting: Meeting | null = null) => {
            if (meeting === null) {
                meeting = this.meetingsStore.getStore$().getValue().activeMeeting;
            }
            const meetingId = meeting?.id;

            if (!meetingId) {
                return;
            }

            if (
                meeting?.last_message?.type === 'call_event' &&
                meeting.last_message.content.event_type === 'unanswered'
            ) {
                this.meetingsStore.dispatchIncrementMissedCallsCount(meetingId);
            } else {
                this.meetingsStore.dispatchIncrementUnreadCount(meetingId);
            }
        };

        const markMessagesAsRead = (meeting: Meeting | null = null) => {
            if (meeting === null) {
                meeting = this.meetingsStore.getStore$().getValue().activeMeeting;
            }
            const meetingId = meeting?.id;

            if (!meetingId) {
                return;
            }

            this.meetingsStore.dispatchSetUnreadCount(meetingId, 0);
            this.meetingsStore.dispatchSetMissedCallsCount(meetingId, 0);
            this.chatMeetingService.updateMeetingLastSeenAt(meetingId);
        };

        // New messages either bump the unread count or mark meeting as read.
        newMessageInAMeeting$.subscribe((meeting) => {
            if (windowIsFocused() && meetingIsActive(meeting) && isStuckToBottom()) {
                markMessagesAsRead(meeting);
            } else {
                bumpLocalCounter(meeting);
            }
        });

        // If someone scrolls to the bottom, no matter what, mark active meeting as read.
        // It shouldn't matter if a window is not active.
        isStuckToBottom$.subscribe((isStuck) => {
            if (isStuck) markMessagesAsRead();
        });

        // When we change the active meeting, probably mark it as read too?
        activeMeeting$.subscribe((meeting) => markMessagesAsRead(meeting));

        // When focusing a window, mark messages as read **after a while**,
        // so that the counter does not disappear immediately.
        windowIsFocused$
            .filter((isFocused) => isFocused)
            .filter(isStuckToBottom)
            .delay(2000)
            .subscribe(() => markMessagesAsRead());
    }

    protected initializeStore() {
        this.chatMeetingService.getMeetings().subscribe((meetings) => this.meetingsStore.dispatchSetMeetings(meetings));
    }

    setActiveMeeting(meeting: Meeting) {
        this.meetingsStore.dispatchSetActiveMeeting(meeting);
        // exiting search makes only sense if we are in a search mode
        this.conversationService.dispatchExitSearch(meeting.id);
    }

    getMeetings$() {
        return this.meetings$;
    }

    getActiveMeeting$() {
        return this.meetingsStore.activeMeeting$;
    }
}

angular.module('tradersyard').service('ChatService', ChatService);
